/**
 * TimelineSection — Chronological view of audit instances (frozen + projected).
 *
 * Displays inside AuditDetailPage below the ScheduleListSection.
 *
 * Two visual zones:
 *   1. **Frozen** (in DB, has frozen_at): solid card styling, snowflake icon.
 *      These are locked in — schedule changes won't affect them.
 *   2. **Projected** (computed client-side from schedule patterns): dashed/ghost
 *      styling, "Planned" label. Not clickable, for preview only.
 *
 * Features:
 * - Status badges with color coding (pending, in_progress, completed, overdue, skipped, cancelled)
 * - Status filter pills
 * - Actions on frozen instances: skip, cancel, reassign auditor, delete
 * - Info popover explaining frozen vs projected concept
 * - Loading skeleton
 */

import { useState, useMemo } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import { useInstancesByAuditId } from '../hooks/useScheduleInstances';
import { useSchedulesByAuditId } from '../hooks/useSchedules';
import type {
  LpaScheduleInstanceWithDetails,
  LpaScheduleWithDetails,
  LpaInstanceStatus,
  LpaScheduleFrequency,
} from '../types';
import InstanceStatusDialog from './InstanceStatusDialog';
import InstanceAuditorDialog from './InstanceAuditorDialog';
import InstanceDeleteDialog from './InstanceDeleteDialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Clock,
  Snowflake,
  CalendarDays,
  HelpCircle,
  MoreHorizontal,
  Ban,
  SkipForward,
  UserRoundPen,
  Trash2,
  ListChecks,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Instance status config
// ---------------------------------------------------------------------------

const INSTANCE_STATUS_CONFIG: Record<
  LpaInstanceStatus,
  { labelKey: string; fallback: string; className: string; dotColor: string }
> = {
  pending: {
    labelKey: 'mod_lpa.instances.status.pending',
    fallback: 'Pending',
    className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    dotColor: 'bg-slate-400',
  },
  in_progress: {
    labelKey: 'mod_lpa.instances.status.inProgress',
    fallback: 'In Progress',
    className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    dotColor: 'bg-blue-500',
  },
  completed: {
    labelKey: 'mod_lpa.instances.status.completed',
    fallback: 'Completed',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
    dotColor: 'bg-emerald-500',
  },
  overdue: {
    labelKey: 'mod_lpa.instances.status.overdue',
    fallback: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    dotColor: 'bg-red-500',
  },
  skipped: {
    labelKey: 'mod_lpa.instances.status.skipped',
    fallback: 'Skipped',
    className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    dotColor: 'bg-amber-500',
  },
  cancelled: {
    labelKey: 'mod_lpa.instances.status.cancelled',
    fallback: 'Cancelled',
    className: 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700',
    dotColor: 'bg-gray-400',
  },
};

const FREQUENCY_CONFIG: Record<
  LpaScheduleFrequency,
  { labelKey: string; fallback: string; className: string }
> = {
  per_shift: {
    labelKey: 'mod_lpa.schedules.freq.perShift',
    fallback: 'Per Shift',
    className: 'bg-violet-500/15 text-violet-700 border-violet-300 dark:text-violet-400 dark:border-violet-600',
  },
  daily: {
    labelKey: 'mod_lpa.schedules.freq.daily',
    fallback: 'Daily',
    className: 'bg-blue-500/15 text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-600',
  },
  weekly: {
    labelKey: 'mod_lpa.schedules.freq.weekly',
    fallback: 'Weekly',
    className: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-600',
  },
  monthly: {
    labelKey: 'mod_lpa.schedules.freq.monthly',
    fallback: 'Monthly',
    className: 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-600',
  },
  quarterly: {
    labelKey: 'mod_lpa.schedules.freq.quarterly',
    fallback: 'Quarterly',
    className: 'bg-rose-500/15 text-rose-700 border-rose-300 dark:text-rose-400 dark:border-rose-600',
  },
};

// Status filters — null = show all
const STATUS_FILTERS: (LpaInstanceStatus | null)[] = [
  null,
  'pending',
  'in_progress',
  'completed',
  'overdue',
  'skipped',
  'cancelled',
];

// ---------------------------------------------------------------------------
// Period computation — generates projected instances from schedule patterns
// ---------------------------------------------------------------------------

interface ProjectedInstance {
  type: 'projected';
  scheduleId: string;
  scheduleName: string;
  frequency: LpaScheduleFrequency;
  auditorId: string | null;
  auditorName: string | null;
  auditorEmail: string | null;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
}

interface FrozenInstance {
  type: 'frozen';
  data: LpaScheduleInstanceWithDetails;
}

type TimelineItem = FrozenInstance | ProjectedInstance;

/**
 * Compute the next period boundaries for a given frequency after a reference date.
 */
function getNextPeriod(
  frequency: LpaScheduleFrequency,
  referenceDate: Date,
): { periodStart: Date; periodEnd: Date } {
  const start = new Date(referenceDate);
  start.setHours(0, 0, 0, 0);

  switch (frequency) {
    case 'per_shift':
    case 'daily': {
      const end = new Date(start);
      end.setDate(end.getDate());
      return { periodStart: start, periodEnd: end };
    }
    case 'weekly': {
      // Period starts on Monday
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Adjust to Monday
      const weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() + diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return { periodStart: weekStart, periodEnd: weekEnd };
    }
    case 'monthly': {
      const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
      const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      return { periodStart: monthStart, periodEnd: monthEnd };
    }
    case 'quarterly': {
      const quarterMonth = Math.floor(start.getMonth() / 3) * 3;
      const qStart = new Date(start.getFullYear(), quarterMonth, 1);
      const qEnd = new Date(start.getFullYear(), quarterMonth + 3, 0);
      return { periodStart: qStart, periodEnd: qEnd };
    }
  }
}

/**
 * Advance a date by one period step based on frequency.
 */
function advancePeriod(date: Date, frequency: LpaScheduleFrequency): Date {
  const next = new Date(date);
  switch (frequency) {
    case 'per_shift':
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'quarterly':
      next.setMonth(next.getMonth() + 3);
      break;
  }
  return next;
}

// TODO(lpa-timezone): Date-only conversions currently use `toISOString()` and
// `new Date('YYYY-MM-DD')` in this file. This can shift days in non-UTC
// timezones. Keep this behavior for now (demo parity), then replace with
// timezone-safe local date helpers in the production generation pass.
function toISODate(d: Date): string {
  return d.toISOString().split('T')[0];
}

/**
 * Generate projected (not-yet-in-DB) instances for active schedules.
 * Projects N periods ahead from the last frozen instance or schedule start_date.
 */
function computeProjectedInstances(
  schedules: LpaScheduleWithDetails[],
  frozenInstances: LpaScheduleInstanceWithDetails[],
  projectCount: number = 4,
): ProjectedInstance[] {
  const projected: ProjectedInstance[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (const schedule of schedules) {
    // Only project for active schedules with an auditor
    if (!schedule.is_active || !schedule.auditor_id) continue;

    // Find the last frozen instance for this schedule (by period_start)
    const scheduleFrozen = frozenInstances
      .filter((i) => i.schedule_id === schedule.id)
      .sort((a, b) => a.period_start.localeCompare(b.period_start));

    // Determine the starting point for projection
    let projectionStart: Date;
    if (scheduleFrozen.length > 0) {
      const lastFrozen = scheduleFrozen[scheduleFrozen.length - 1];
      projectionStart = advancePeriod(new Date(lastFrozen.period_start), schedule.frequency);
    } else {
      // Start from schedule start_date or today, whichever is later
      const scheduleStart = new Date(schedule.start_date);
      projectionStart = scheduleStart > today ? scheduleStart : today;
    }

    // Check schedule end_date
    const scheduleEnd = schedule.end_date ? new Date(schedule.end_date) : null;

    // Generate projected periods
    let count = 0;
    let cursor = projectionStart;
    while (count < projectCount) {
      const period = getNextPeriod(schedule.frequency, cursor);

      // Stop if past end_date
      if (scheduleEnd && period.periodStart > scheduleEnd) break;

      const periodStartStr = toISODate(period.periodStart);
      const periodEndStr = toISODate(period.periodEnd);

      // Check if this period already exists as a frozen instance
      const alreadyExists = scheduleFrozen.some(
        (i) => i.period_start === periodStartStr,
      );

      if (!alreadyExists) {
        // Due date = end of period at EOB (simplified to end of day)
        const dueDate = new Date(period.periodEnd);
        dueDate.setHours(23, 59, 59, 999);

        projected.push({
          type: 'projected',
          scheduleId: schedule.id,
          scheduleName: schedule.name,
          frequency: schedule.frequency,
          auditorId: schedule.auditor_id,
          auditorName: schedule.auditor?.full_name ?? null,
          auditorEmail: schedule.auditor?.email ?? null,
          periodStart: periodStartStr,
          periodEnd: periodEndStr,
          dueDate: dueDate.toISOString(),
        });
        count++;
      }

      cursor = advancePeriod(period.periodStart, schedule.frequency);
    }
  }

  return projected;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

function formatPeriod(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);

  if (start === end) {
    return s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}–${e.getDate()}`;
  }

  return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function formatDueDate(dueDateStr: string): string {
  const due = new Date(dueDateStr);
  return due.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InstanceStatusBadge({
  status,
  t,
}: {
  status: LpaInstanceStatus;
  t: (key: string, fallback: string) => string;
}) {
  const config = INSTANCE_STATUS_CONFIG[status];
  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${config.dotColor}`} />
      {t(config.labelKey, config.fallback)}
    </Badge>
  );
}

function FrozenInstanceRow({
  instance,
  isArchived,
  t,
  onStatusChange,
  onAuditorChange,
  onDelete,
}: {
  instance: LpaScheduleInstanceWithDetails;
  isArchived: boolean;
  t: (key: string, fallback: string) => string;
  onStatusChange: (instance: LpaScheduleInstanceWithDetails) => void;
  onAuditorChange: (instance: LpaScheduleInstanceWithDetails) => void;
  onDelete: (instance: LpaScheduleInstanceWithDetails) => void;
}) {
  const freqConfig = FREQUENCY_CONFIG[instance.schedule.frequency];
  const canModify = !isArchived && (instance.status === 'pending' || instance.status === 'in_progress');
  const canDelete = !isArchived && instance.status === 'pending';

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      {/* Frozen indicator */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Snowflake className="h-4 w-4 text-sky-500 shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            {t('mod_lpa.instances.frozenTooltip', 'Generated & frozen — schedule changes won\'t affect this instance')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Schedule name + frequency */}
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <span className="text-sm font-medium truncate max-w-[140px]">
          {instance.schedule.name}
        </span>
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${freqConfig.className}`}>
          {t(freqConfig.labelKey, freqConfig.fallback)}
        </Badge>
      </div>

      {/* Auditor */}
      <div className="flex items-center gap-1.5 shrink-0">
        <Avatar className="h-5 w-5">
          <AvatarFallback className="text-[10px]">
            {getInitials(instance.auditor.full_name, instance.auditor.email)}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs text-muted-foreground max-w-[100px] truncate">
          {instance.auditor.full_name ?? instance.auditor.email}
        </span>
      </div>

      {/* Period */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
        <CalendarDays className="h-3 w-3" />
        <span>{formatPeriod(instance.period_start, instance.period_end)}</span>
      </div>

      {/* Due date */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
        <Clock className="h-3 w-3" />
        <span>{formatDueDate(instance.due_date)}</span>
      </div>

      {/* Status badge */}
      <div className="shrink-0">
        <InstanceStatusBadge status={instance.status} t={t} />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Actions */}
      {!isArchived && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {canModify && (
              <>
                <DropdownMenuItem onClick={() => onStatusChange(instance)}>
                  <SkipForward className="h-4 w-4 mr-2" />
                  {t('mod_lpa.instances.actions.changeStatus', 'Change Status')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAuditorChange(instance)}>
                  <UserRoundPen className="h-4 w-4 mr-2" />
                  {t('mod_lpa.instances.actions.reassignAuditor', 'Reassign Auditor')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {canDelete && (
              <DropdownMenuItem
                onClick={() => onDelete(instance)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('mod_lpa.instances.actions.delete', 'Delete')}
              </DropdownMenuItem>
            )}
            {!canModify && !canDelete && (
              <DropdownMenuItem disabled>
                <Ban className="h-4 w-4 mr-2" />
                {t('mod_lpa.instances.actions.noActions', 'No actions available')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ProjectedInstanceRow({
  item,
  t,
}: {
  item: ProjectedInstance;
  t: (key: string, fallback: string) => string;
}) {
  const freqConfig = FREQUENCY_CONFIG[item.frequency];

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-dashed border-muted-foreground/20 bg-muted/10 opacity-70">
      {/* Planned indicator */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <CalendarDays className="h-4 w-4 text-muted-foreground/50 shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            {t('mod_lpa.instances.projectedTooltip', 'Projected — not yet generated. Will become real on next generation.')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Schedule name + frequency */}
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <span className="text-sm font-medium truncate max-w-[140px] text-muted-foreground">
          {item.scheduleName}
        </span>
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 opacity-60 ${freqConfig.className}`}>
          {t(freqConfig.labelKey, freqConfig.fallback)}
        </Badge>
      </div>

      {/* Auditor */}
      {item.auditorId && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Avatar className="h-5 w-5 opacity-60">
            <AvatarFallback className="text-[10px]">
              {getInitials(item.auditorName, item.auditorEmail ?? '')}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground/60 max-w-[100px] truncate">
            {item.auditorName ?? item.auditorEmail}
          </span>
        </div>
      )}

      {/* Period */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground/60 shrink-0">
        <CalendarDays className="h-3 w-3" />
        <span>{formatPeriod(item.periodStart, item.periodEnd)}</span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Planned badge */}
      <Badge variant="outline" className="text-[10px] border-dashed text-muted-foreground/50">
        {t('mod_lpa.instances.planned', 'Planned')}
      </Badge>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-[44px] w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyState({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg border border-dashed">
      <ListChecks className="h-7 w-7 text-muted-foreground/50 mb-2" />
      <p className="text-sm text-muted-foreground">
        {t('mod_lpa.instances.empty', 'No instances yet. Generate instances from the schedule detail page, or they will appear here once automatically generated.')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TimelineSectionProps {
  auditId: string;
  isArchived?: boolean;
}

export default function TimelineSection({
  auditId,
  isArchived = false,
}: TimelineSectionProps) {
  const { t } = useLanguage();

  // Data
  const { data: frozenInstances, isLoading: instancesLoading } = useInstancesByAuditId(auditId);
  const { data: schedules, isLoading: schedulesLoading } = useSchedulesByAuditId(auditId);

  const isLoading = instancesLoading || schedulesLoading;

  // Filter state
  const [statusFilter, setStatusFilter] = useState<LpaInstanceStatus | null>(null);

  // Action dialog state
  const [statusDialogInstance, setStatusDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);
  const [auditorDialogInstance, setAuditorDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);
  const [deleteDialogInstance, setDeleteDialogInstance] =
    useState<LpaScheduleInstanceWithDetails | null>(null);

  // Compute projected instances
  const projectedInstances = useMemo(() => {
    if (!schedules || !frozenInstances) return [];
    return computeProjectedInstances(schedules, frozenInstances, 3);
  }, [schedules, frozenInstances]);

  // Build unified timeline
  const timelineItems = useMemo((): TimelineItem[] => {
    const items: TimelineItem[] = [];

    // Add frozen instances
    let filteredFrozen = frozenInstances ?? [];
    if (statusFilter) {
      filteredFrozen = filteredFrozen.filter((i) => i.status === statusFilter);
    }
    for (const inst of filteredFrozen) {
      items.push({ type: 'frozen', data: inst });
    }

    // Add projected instances (only when no status filter or filter is pending)
    if (!statusFilter || statusFilter === 'pending') {
      for (const proj of projectedInstances) {
        items.push(proj);
      }
    }

    // Sort by period_start ascending
    items.sort((a, b) => {
      const aStart = a.type === 'frozen' ? a.data.period_start : a.periodStart;
      const bStart = b.type === 'frozen' ? b.data.period_start : b.periodStart;
      const cmp = aStart.localeCompare(bStart);
      if (cmp !== 0) return cmp;
      // Frozen before projected for same period
      if (a.type === 'frozen' && b.type === 'projected') return -1;
      if (a.type === 'projected' && b.type === 'frozen') return 1;
      return 0;
    });

    return items;
  }, [frozenInstances, projectedInstances, statusFilter]);

  // Count instances by status (for filter badges)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const inst of frozenInstances ?? []) {
      counts[inst.status] = (counts[inst.status] ?? 0) + 1;
    }
    return counts;
  }, [frozenInstances]);

  const totalFrozen = frozenInstances?.length ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t('mod_lpa.instances.timelineTitle', 'Instance Timeline')}
            </CardTitle>
            {totalFrozen > 0 && (
              <Badge variant="secondary" className="text-xs">
                {totalFrozen}
              </Badge>
            )}
            {/* Info popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" className="w-80 text-sm">
                <div className="space-y-2">
                  <p className="font-medium">
                    {t('mod_lpa.instances.helpTitle', 'Frozen vs Projected')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'mod_lpa.instances.timelineDescription',
                      'Chronological view of all audit instances. Frozen instances are locked; projected ones show future occurrences based on schedule patterns.',
                    )}
                  </p>
                  <div className="flex items-start gap-2">
                    <Snowflake className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'mod_lpa.instances.helpFrozen',
                        'Frozen instances are locked in the database. Changes to the schedule (auditor, frequency, questions) will NOT affect them.',
                      )}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'mod_lpa.instances.helpProjected',
                        'Projected instances are computed from the schedule pattern for preview. They become frozen once generated.',
                      )}
                    </p>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <TimelineSkeleton />
        ) : (
          <div className="space-y-3">
            {/* Status filter pills */}
            {totalFrozen > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUS_FILTERS.map((status) => {
                  const isActive = statusFilter === status;
                  const count =
                    status === null
                      ? totalFrozen
                      : statusCounts[status] ?? 0;

                  // Don't show filters with 0 count (except "All")
                  if (status !== null && count === 0) return null;

                  const label =
                    status === null
                      ? t('mod_lpa.instances.filter.all', 'All')
                      : t(
                          INSTANCE_STATUS_CONFIG[status].labelKey,
                          INSTANCE_STATUS_CONFIG[status].fallback,
                        );

                  return (
                    <Button
                      key={status ?? 'all'}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 text-xs px-2.5"
                      onClick={() => setStatusFilter(status)}
                    >
                      {label}
                      <span className="ml-1.5 opacity-60">{count}</span>
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Timeline rows */}
            {timelineItems.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <div className="space-y-1.5">
                {timelineItems.map((item, idx) =>
                  item.type === 'frozen' ? (
                    <FrozenInstanceRow
                      key={item.data.id}
                      instance={item.data}
                      isArchived={isArchived}
                      t={t}
                      onStatusChange={setStatusDialogInstance}
                      onAuditorChange={setAuditorDialogInstance}
                      onDelete={setDeleteDialogInstance}
                    />
                  ) : (
                    <ProjectedInstanceRow
                      key={`proj-${item.scheduleId}-${item.periodStart}-${idx}`}
                      item={item}
                      t={t}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Action dialogs */}
      <InstanceStatusDialog
        instance={statusDialogInstance}
        onClose={() => setStatusDialogInstance(null)}
        auditId={auditId}
      />
      <InstanceAuditorDialog
        instance={auditorDialogInstance}
        onClose={() => setAuditorDialogInstance(null)}
        auditId={auditId}
      />
      <InstanceDeleteDialog
        instance={deleteDialogInstance}
        onClose={() => setDeleteDialogInstance(null)}
        auditId={auditId}
      />
    </Card>
  );
}
